// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Impact detection on microphone metering.
 *
 * A club striking a ball is one of the sharpest sounds on a golf course: a
 * broadband transient with a rise time of a couple of milliseconds, far louder
 * up close than anything else the round produces. Full DSP would look at the
 * waveform; this detector works on what expo-av actually exposes — a dB
 * metering value per progress-update interval (~100ms) — which is enough,
 * because even averaged over 100ms the strike stands well proud of an outdoor
 * baseline.
 *
 * Two rules separate a strike from wind swell and speech:
 *
 * - Size: the sample must jump at least `minRiseDb` above the rolling baseline
 *   (median of the recent window — median so the spike itself and short gusts
 *   cannot drag the reference up).
 * - Speed: the PREVIOUS sample must still have been near the baseline. Wind
 *   and conversation build over several samples; a strike goes from quiet to
 *   peak inside one interval.
 *
 * Everything here is pure and synchronous so it can be unit-tested by feeding
 * synthetic metering series. The caller owns the microphone.
 */

export type MeteringSample = {
  /** Milliseconds, any monotonic clock. */
  ts: number;
  /** Metering level in dBFS as reported by the recorder (typically -160..0). */
  db: number;
};

export type ImpactAudioEvent = {
  /** Timestamp of the sample that triggered. */
  ts: number;
  /** How far above baseline the trigger sample was. */
  riseDb: number;
};

/** Per-sample detector internals, for debug instrumentation and calibration. */
export type ImpactAudioDiagnostic = {
  ts: number;
  db: number;
  baseline: number;
  /** How far above baseline this sample sat — the calibration number. */
  rise: number;
};

export type ImpactAudioOptions = {
  /** How much history the baseline median looks at. */
  baselineWindowMs?: number;
  /** Samples required before the detector will trigger at all. */
  minSamplesForBaseline?: number;
  /** Required jump above baseline. */
  minRiseDb?: number;
  /** The previous sample may sit at most this far above baseline ("fast rise"). */
  maxPrevRiseDb?: number;
  /** Dead time after a trigger; one swing never contains two strikes. */
  refractoryMs?: number;
  /**
   * Called for every sample once a baseline exists — including samples that do
   * NOT trigger. This is how field calibration sees the sub-threshold world:
   * a putt that only managed +9 dB is invisible in trigger events but is
   * exactly the number that decides whether putts are detectable at all.
   */
  onDiagnostic?: (info: ImpactAudioDiagnostic) => void;
};

export const IMPACT_AUDIO_DEFAULTS: Required<Omit<ImpactAudioOptions, 'onDiagnostic'>> = {
  baselineWindowMs: 5000,
  minSamplesForBaseline: 8,
  minRiseDb: 14,
  maxPrevRiseDb: 6,
  refractoryMs: 2500,
};

export type ImpactAudioDetector = {
  /** Feed one metering sample; returns an event when this sample is a strike. */
  feed: (sample: MeteringSample) => ImpactAudioEvent | null;
  reset: () => void;
};

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

export const createImpactAudioDetector = (options?: ImpactAudioOptions): ImpactAudioDetector => {
  const config = { ...IMPACT_AUDIO_DEFAULTS, ...options };
  let window: MeteringSample[] = [];
  let lastImpactTs: number | null = null;

  const feed = (sample: MeteringSample): ImpactAudioEvent | null => {
    if (!Number.isFinite(sample.db) || !Number.isFinite(sample.ts)) {
      return null;
    }

    const previous = window.length > 0 ? window[window.length - 1] : null;

    // Baseline from history only — the trigger sample must not vote on the
    // reference it is measured against.
    const history = window.filter((entry) => sample.ts - entry.ts <= config.baselineWindowMs);
    const baseline = history.length >= config.minSamplesForBaseline
      ? median(history.map((entry) => entry.db))
      : null;

    window = [...history, sample];

    if (baseline !== null) {
      config.onDiagnostic?.({
        ts: sample.ts,
        db: sample.db,
        baseline,
        rise: sample.db - baseline,
      });
    }

    if (baseline === null || previous === null) {
      return null;
    }

    if (lastImpactTs !== null && sample.ts - lastImpactTs < config.refractoryMs) {
      return null;
    }

    const rise = sample.db - baseline;
    const prevRise = previous.db - baseline;
    if (rise >= config.minRiseDb && prevRise <= config.maxPrevRiseDb) {
      lastImpactTs = sample.ts;
      return { ts: sample.ts, riseDb: rise };
    }

    return null;
  };

  return {
    feed,
    reset: () => {
      window = [];
      lastImpactTs = null;
    },
  };
};
